import React, { useState, useEffect, useMemo } from "react";
import { UI_COLORS } from "../../config";
import SelectKeyPlaceholder from "./SelectKeyPlaceholder";
import ValueLoadingPlaceholder from "./ValueLoadingPlaceholder";

import ValueErrorPlaceholder from "./ValueErrorPlaceholder";
import {
  Button,
  Col,
  Descriptions,
  Empty,
  Form,
  Input,
  notification,
  Row,
  Spin,
  Typography,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faDownload,
  faFloppyDisk,
  faKey,
  faPen,
  faQrcode,
  faTag,
  faUpRightFromSquare,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../i18n";
import { useAppManager } from "../../hooks/useAppManager";
/**
 * Component for displaying the details of a key and its value
 */
function KeyDetail({
  keyData,
  value,
  isLoading,
  error,
  qrCodeUrl,
  onUpdateValue,
  isUpdating,
  updateError,
  updateSuccess,
}) {
  const { t } = useTranslation();
  const { userActions } = useAppManager();

  // Check user permissions
  const canUpdate = useMemo(
    () => userActions?.includes("dpp-qr-update"),
    [userActions]
  );
    const canRead = useMemo(
    () => userActions?.includes("dpp-qr-read"),
    [userActions]
  );
  const [isEditing, setIsEditing] = useState(false);
  const [isDownloadingQR, setIsDownloadingQR] = useState(false);
  const [editValue, setEditValue] = useState("");
  const [qrRefreshKey, setQrRefreshKey] = useState(0);
  const [form] = Form.useForm();
  const [api, contextHolder] = notification.useNotification();
  // Reset editing state when key changes
  
  useEffect(() => {
    setIsEditing(false);
    if (value && value.rina_id) {
      setEditValue(
        typeof value.rina_id === "object"
          ? JSON.stringify(value.rina_id, null, 2)
          : value.rina_id
      );
    }
  }, [keyData, value]);

  // Refresh QR code when update is successful
  useEffect(() => {
    if (updateSuccess) {
      // Increment the key to force the image to reload
      setQrRefreshKey((prev) => prev + 1);
    }
  }, [updateSuccess]);
  // show notifications when updateError / updateSuccess change
  useEffect(() => {
    if (updateError) {
      api.error({
        message: t("keyDetail.updateFailed"),
        description: updateError,
        showProgress: true,
        pauseOnHover: false,
      });
    }
  }, [updateError, api, t]);

  useEffect(() => {
    if (updateSuccess) {
      api.success({
      message: t("keyDetail.updateSuccess"),
      description: t("keyDetail.updateSuccessDescription", { key: keyData }),
        showProgress: true,
        pauseOnHover: false,
      });
    }
  }, [updateSuccess, api, t, keyData]);
  // Handle QR code download
  const handleDownloadQR = async () => {
    try {
      setIsDownloadingQR(true);
      // Fetch the image as blob
      const response = await fetch(qrCodeUrl);
      const blob = await response.blob();

      // Create a download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `qr-code-${value.id}.png`;

      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Error downloading QR code:", error);
    } finally {
      setIsDownloadingQR(false);
    }
  };
  function FieldLabel({ icon, text }) {
    return (
      <span className="font-bold">
        <FontAwesomeIcon icon={icon} size="sm" style={{ marginRight: 4 }} />
        {text}
      </span>
    );
  }

  // Check read permission first
  if (!canRead) {
    return <SelectKeyPlaceholder noPermission={true} />;
  }

  if (!value) {

    return <SelectKeyPlaceholder noPermission={false} />;
  }

  if (isLoading) {
return <ValueLoadingPlaceholder tip={t("keyDetail.loadingValue", { id: value.id })} />;
  }

  if (error) {
    return (
      <ValueErrorPlaceholder
        title={t("keyDetail.errorLoadingData")}
        description={undefined}
      />
    );
  }

  const handleSubmit = async () => {
    if (onUpdateValue) await onUpdateValue(editValue);
  };
  const valueLabel = isEditing ? (
    <div className="flex items-center justify-between">
      <div>
        <FieldLabel icon={faTag} text={t("keyDetail.value")} />
      </div>
      <div>
        <Button
          type="primary"
          size="small"
          style={{
            backgroundColor: UI_COLORS.primary,
            color: UI_COLORS.white,
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
            marginRight: 5,
          }}
          onClick={() => form.submit()}
          loading={isUpdating}
          icon={<FontAwesomeIcon icon={faFloppyDisk} />}
        >
          {t("keyDetail.save")}
        </Button>
        <Button
          style={{
            backgroundColor: "#cf1322",
            color: UI_COLORS.white,
            border: "none",
            cursor: "pointer",
            transition: "all 0.3s",
          }}
          disabled={isUpdating}
          size="small"
          onClick={() => {
            setIsEditing(false);
            /* restore editValue from value */ setEditValue(
              typeof value?.value === "object"
                ? JSON.stringify(value.value, null, 2)
                : value?.value || ""
            );
          }}
        >
          {t("keyDetail.cancel")}
        </Button>
      </div>
    </div>
  ) : (
    <div className="flex items-center justify-between">
      <FieldLabel icon={faTag} text={t("keyDetail.value")} />
    {canUpdate && (
      <Button
        style={{
          backgroundColor: UI_COLORS.primary,
          color: UI_COLORS.white,
          border: "none",
          cursor: "pointer",
          transition: "all 0.3s",
        }}
        size="small"
        icon={<FontAwesomeIcon icon={faPen} />}
        onClick={() => setIsEditing(true)}
        disabled={isUpdating || !value}
      >
        {t("keyDetail.editValue")}
      </Button>
    )}
    </div>
  );

  const valueContent = isEditing ? (
    <Form
      form={form}
      onFinish={handleSubmit}
      style={{ flex: 1, display: "flex", flexDirection: "column" }}
    >
      <Form.Item style={{ flex: 1, marginBottom: 5 }}>
        <Input
          variant="borderless"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="font-mono text-sm"
          disabled={!isEditing || isUpdating}
          placeholder={t("keyDetail.valuePlaceholder")}
        />
        {isUpdating && (
          <div className="absolute inset-0 bg-white bg-opacity-60 flex items-center justify-center mt-0">
            <Spin />
          </div>
        )}
      </Form.Item>
    </Form>
  ) : (
    <Typography.Text copyable={{ text: editValue }} className="font-mono">
      {editValue}
    </Typography.Text>
  );
  return (
    <div className="p-4 h-full">
      {contextHolder}
      <Row gutter={16} className="h-full">
        <Col xs={24} xl={11} className="h-full">
          <Descriptions column={1} size="small" bordered layout="vertical">
            <Descriptions.Item label={<FieldLabel icon={faKey} text={t("keyDetail.key")} />}>
              <Typography.Text
                copyable={{ text: value.id }}
                className="font-mono"
              >
                {value.id}
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item
              label={<FieldLabel icon={faBuilding} text={t("keyDetail.tenant")} />}
            >
              <Typography.Text className="font-mono">
                {value.tenant}
              </Typography.Text>
            </Descriptions.Item>

            <Descriptions.Item
              label={<FieldLabel icon={faKey} text={t("keyDetail.fullKey")} />}
            >
              <Typography.Text className="font-mono break-words">
                {value.tenant}:{value.id}
              </Typography.Text>
            </Descriptions.Item>

            {value?.redirect_url && (
              <Descriptions.Item
                label={
                  <FieldLabel
                    icon={faUpRightFromSquare}
                    text={t("keyDetail.redirectUrl")}
                  />
                }
              >
                <Typography.Link
                  href={value.redirect_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: UI_COLORS.primary }}
                >
                  {value.redirect_url}
                </Typography.Link>
              </Descriptions.Item>
            )}
            <Descriptions.Item label={valueLabel}>
              {valueContent}
            </Descriptions.Item>
          </Descriptions>
        </Col>

        {/* Right: QR code */}
        <Col xs={24} xl={13} className="h-full">
          <Descriptions column={1} size="small" bordered layout="vertical">
            <Descriptions.Item
              label={
                <div className="flex items-center justify-between">
                  <FieldLabel icon={faQrcode} text={t("keyDetail.qrCode")} />
                  <span>
                    <Button
                      style={{
                        backgroundColor: UI_COLORS.primary,
                        color: UI_COLORS.white,
                        border: "none",
                        cursor: "pointer",
                        transition: "all 0.3s",
                      }}
                      size="small"
                      icon={<FontAwesomeIcon icon={faDownload} />}
                      loading={isDownloadingQR}
                      onClick={handleDownloadQR}
                    >
                      {t("keyDetail.download")}
                    </Button>
                    {value?.redirect_url && (
                      <Button
                        style={{
                          backgroundColor: UI_COLORS.primary,
                          color: UI_COLORS.white,
                          border: "none",
                          cursor: "pointer",
                          transition: "all 0.3s",
                          marginLeft: 5,
                        }}
                        size="small"
                        icon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                        onClick={() => {
                          if (value?.redirect_url) {
                            window.open(
                              value.redirect_url,
                              "_blank",
                              "noopener,noreferrer"
                            );
                          }
                        }}
                      >
                        {t("keyDetail.openLink")}
                      </Button>
                    )}
                  </span>
                </div>
              }
            >
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {qrCodeUrl ? (
                  <>
                    <img
                      key={`qr-image-${value.id}-${qrRefreshKey}`}
                      src={qrCodeUrl}
                      alt={`QR Code for ${value.id}`}
                      style={{ maxWidth: "90%", maxHeight: "90%" }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect width='200' height='200' fill='%23f8f8f8'/%3E%3Ctext x='50%' y='50%' font-family='Arial' font-size='12' text-anchor='middle' dominant-baseline='middle' fill='%23888'%3EFailed to load QR code%3C/text%3E%3C/svg%3E";
                      }}
                    />
                  </>
                ) : (
                  <Empty description={t("keyDetail.qrNotAvailable")} />
                )}
              </div>

              {/* <div style={{ marginTop: 12, textAlign: "center" }}>
                <p className="text-sm text-gray-500">
                  Scan to access the redirect link
                </p>
              </div>

              {value?.redirect_url && (
                <div style={{ marginTop: 12, textAlign: "center" }}>
                  <a
                    href={value.redirect_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: UI_COLORS.primary }}
                  >
                    Open link
                  </a>
                </div>
              )} */}
            </Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
    </div>
  );
}

export default KeyDetail;
